import { getFragmentData } from 'app/generated/gql'
import { modelData } from 'app/lib/modelData'
import { CheckCircleSolid, NavArrowDown, NavArrowRight as CaretRightIcon } from 'iconoir-react'
import classNames from 'classnames'
import moment from 'moment'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useSidebarCounts } from '../../hooks/useSidebarCounts'
import { LazyContactCell } from '../LazyContactCell'
import { TaskAssigneeLabel } from 'app/components/tasks/TaskAssigneeLabel'
import { taskAssigneeValue } from 'lib/taskAssignee'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu'
import { Sheet, SheetContent } from '../ui/sheet'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { TableEmptyState } from '../ui/table-empty-state'
import { QuickCreateTaskModal } from './QuickCreateTaskModal'
import { TaskDetailPane, type ContactInfo, type TaskFieldInput } from './TaskDetailPane'
import type { ContactJson, TaskJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { gqlFetchList, gqlMutation } from 'app/lib/graphqlClient'
import { dueDateAsDay } from 'app/lib/taskDueDate'
import { bg } from 'lib/background'
import { DELETE_TASK_MUTATION, TASK_FIELDS, TASK_FIELDS_FRAGMENT, UPDATE_TASK_MUTATION } from './graphql'

// ─── Status config ────────────────────────────────────────────────────────────
// Only two states for now: Open / Complete. Status pill shows colored dot + label;
// click toggles between the two.

const STATUS_CONFIG = {
  open: { label: `Open`, dot: `bg-blue-500`, pill: `border-blue-200 bg-blue-50 text-blue-700` },
  closed: { label: `Complete`, dot: `bg-green-500`, pill: `border-green-200 bg-green-50 text-green-800` },
} as const

type StatusKey = keyof typeof STATUS_CONFIG

// ─── Helpers ──────────────────────────────────────────────────────────────────

function startOfToday() {
  return moment().startOf(`day`)
}
function endOfThisWeek() {
  return startOfToday().add(7 - moment().day(), `days`)
}
function isOverdue(task: TaskJson) {
  if (!task.dueDate || task.status === `closed`) return false
  return dueDateAsDay(task.dueDate).isBefore(startOfToday())
}
function isDueToday(task: TaskJson) {
  if (!task.dueDate) return false
  return dueDateAsDay(task.dueDate).isSame(startOfToday())
}
function isDueThisWeek(task: TaskJson) {
  if (!task.dueDate) return false
  const d = dueDateAsDay(task.dueDate)
  return d.isSameOrAfter(startOfToday()) && d.isSameOrBefore(endOfThisWeek())
}
function formatDueDate(dueDate?: string): string {
  if (!dueDate) return ``
  const d = dueDateAsDay(dueDate)
  const today = startOfToday()
  const diff = d.diff(today, `days`)
  if (diff === 0) return `Today`
  if (diff === 1) return `Tomorrow`
  if (diff === -1) return `Yesterday`
  if (diff > 1 && diff < 7) return d.format(`ddd`)
  if (diff < -1 && diff > -7) return `${Math.abs(diff)}d ago`
  if (d.year() === today.year()) return d.format(`MMM D`)
  return d.format(`MMM D, YYYY`)
}
function initials(name?: string | null): string {
  return (name || `?`)
    .split(` `)
    .map((n) => n[0])
    .join(``)
    .slice(0, 2)
    .toUpperCase()
}

// ─── Grouping ─────────────────────────────────────────────────────────────────

export type GroupBy = `due` | `status` | `owner` | `none`

type GroupMarker =
  | { type: `due`; dueKey: `overdue` | `today` | `week` | `later` | `none` }
  | { type: `status`; statusKey: StatusKey }
  | { type: `owner`; ownerId?: string | null; roleId?: string | null }

type Group = {
  key: string
  label: string
  sortIndex: number
  marker: GroupMarker
  tasks: TaskJson[]
}

// Color progression maps urgency:
//   overdue → red (past)
//   today   → blue (now)
//   week    → amber (heads-up, within current week)
//   later   → gray (no urgency)
//   none    → light gray (no date at all)
const DUE_DOT: Record<string, string> = {
  overdue: `bg-red-500`,
  today: `bg-blue-500`,
  week: `bg-amber-500`,
  later: `bg-(--fg-minimal)`,
  none: `bg-(--line-strong)`,
}

function buildGroups(tasks: TaskJson[], groupBy: GroupBy): Group[] | null {
  if (groupBy === `none`) return null
  const map = new Map<string, Group>()
  const ensure = (key: string, label: string, sortIndex: number, marker: GroupMarker): Group => {
    if (!map.has(key)) map.set(key, { key, label, sortIndex, marker, tasks: [] })
    return map.get(key)!
  }

  for (const t of tasks) {
    if (groupBy === `due`) {
      let key: 'overdue' | 'today' | 'week' | 'later' | 'none'
      let label: string
      let idx: number
      if (isOverdue(t)) {
        key = `overdue`
        label = `Overdue`
        idx = 0
      } else if (isDueToday(t)) {
        key = `today`
        label = `Today`
        idx = 1
      } else if (isDueThisWeek(t)) {
        key = `week`
        label = `This week`
        idx = 2
      } else if (t.dueDate) {
        key = `later`
        label = `Later`
        idx = 3
      } else {
        key = `none`
        label = `No due date`
        idx = 4
      }
      ensure(key, label, idx, { type: `due`, dueKey: key }).tasks.push(t)
    } else if (groupBy === `status`) {
      const key = (t.status === `closed` ? `closed` : `open`) as StatusKey
      const cfg = STATUS_CONFIG[key]
      ensure(key, cfg.label, key === `open` ? 0 : 1, { type: `status`, statusKey: key }).tasks.push(t)
    } else if (groupBy === `owner`) {
      const key = taskAssigneeValue(t) ?? `__unassigned__`
      ensure(key, `—`, key === `__unassigned__` ? 9999 : 0, {
        type: `owner`,
        ownerId: t.ownerId,
        roleId: t.roleId,
      }).tasks.push(t)
    }
  }

  return [...map.values()].sort((a, b) => a.sortIndex - b.sortIndex || a.label.localeCompare(b.label))
}

// ─── Status badge ─────────────────────────────────────────────────────────────
// Bordered button trigger (dot + label + chevron) that opens a portaled menu
// with Open / Complete. Mirrors the drawer's status control exactly so the
// table and drawer feel like the same surface.

function StatusBadge({ status, onToggle }: { status: StatusKey; onToggle: () => void }) {
  const isClosed = status === `closed`
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="group inline-flex h-7 items-center gap-2 rounded-md border border-default bg-surface-raised pl-2 pr-1.5 text-body-sm text-strong shadow-elevation-1 transition-colors hover:border-strong hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-default/30"
          aria-label="Change status"
        >
          <span className={classNames(`h-1.5 w-1.5 rounded-full`, isClosed ? `bg-green-600` : `bg-blue-600`)} />
          <span className="leading-none">{isClosed ? `Complete` : `Open`}</span>
          <NavArrowDown
            className="h-3.5 w-3.5 text-minimal transition-colors group-hover:text-subtle"
            strokeWidth={2}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="min-w-[160px]"
        // Row click navigates; suppress propagation from menu interactions.
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuItem
          onSelect={() => {
            if (isClosed) onToggle()
          }}
          className="gap-2 text-body-sm"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
          <span className="flex-1">Open</span>
          {!isClosed && <CheckCircleSolid className="h-3.5 w-3.5 text-minimal" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            if (!isClosed) onToggle()
          }}
          className="gap-2 text-body-sm"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
          <span className="flex-1">Complete</span>
          {isClosed && <CheckCircleSolid className="h-3.5 w-3.5 text-minimal" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ─── Group marker (small dot/avatar before label) ─────────────────────────────

function GroupMarkerDot({ marker }: { marker: GroupMarker }) {
  if (marker.type === `due`) {
    return <span className={classNames(`h-2 w-2 shrink-0 rounded-full`, DUE_DOT[marker.dueKey])} aria-hidden />
  }
  if (marker.type === `status`) {
    return (
      <span className={classNames(`h-2 w-2 shrink-0 rounded-full`, STATUS_CONFIG[marker.statusKey].dot)} aria-hidden />
    )
  }
  // owner
  return <span className="h-2 w-2 shrink-0 rounded-full bg-(--line-strong)" aria-hidden />
}

// ─── Group header label for owner type (async lookup) ─────────────────────────

function OwnerGroupLabel({ ownerId, roleId }: { ownerId?: string | null; roleId?: string | null }) {
  return (
    <span className="inline-flex items-center [&>div]:!p-0 [&_span]:font-semibold">
      <TaskAssigneeLabel ownerId={ownerId} roleId={roleId} />
    </span>
  )
}

// ─── Column layout ───────────────────────────────────────────────────────────
// Shared between the sticky header table and each group's table so columns line
// up across all the cards.

const COL_STATUS = `w-[200px]`
const COL_TASK = ``
const COL_CONTACT = `w-[220px]`
const COL_ASSIGNEE = `w-[180px]`
const COL_DUE = `w-[150px]`

function TaskTableColGroup() {
  return (
    <colgroup>
      <col className={COL_STATUS} />
      <col />
      <col className={COL_CONTACT} />
      <col className={COL_ASSIGNEE} />
      <col className={COL_DUE} />
    </colgroup>
  )
}

function TaskHeaderCells() {
  // Header styling mirrors the main TableComponent used by the contacts /
  // activities tables (`TableComponent.tsx`): `text-ui-xs font-bold text-trn-700`.
  // No uppercase, no wider tracking — sentence case with bold weight is the
  // app-wide convention.
  return (
    <TableRow className="bg-surface-subtle hover:bg-surface-subtle">
      <TableHead className="px-5 text-ui-xs font-bold text-default">Status</TableHead>
      <TableHead className="px-5 text-ui-xs font-bold text-default">Task</TableHead>
      <TableHead className="px-5 text-ui-xs font-bold text-default">Contact</TableHead>
      <TableHead className="px-5 text-ui-xs font-bold text-default">Assignee</TableHead>
      <TableHead className="pl-5 pr-6 text-ui-xs font-bold text-default">Due</TableHead>
    </TableRow>
  )
}

function TaskRow({
  task,
  onClick,
  onToggleStatus,
  isSelected,
}: {
  task: TaskJson
  onClick: () => void
  onToggleStatus: () => void
  isSelected?: boolean
}) {
  const status: StatusKey = task.status === `closed` ? `closed` : `open`
  const overdue = isOverdue(task)
  const closed = status === `closed`

  return (
    <TableRow
      onClick={onClick}
      data-selected={isSelected || undefined}
      data-task-id={task.id}
      className={classNames(
        `group cursor-pointer transition-colors`,
        isSelected ? `bg-[rgba(59,130,246,0.08)] hover:bg-[rgba(59,130,246,0.12)]` : `hover:bg-surface-subtle`,
        closed && !isSelected && `opacity-60`,
      )}
    >
      <TableCell className="px-5 py-3.5">
        <StatusBadge status={status} onToggle={onToggleStatus} />
      </TableCell>
      <TableCell className="px-5 py-3.5">
        <span
          className={classNames(
            `block truncate text-body-sm`,
            closed ? `text-minimal line-through` : `font-medium text-strong`,
          )}
          title={task.title}
        >
          {task.title}
        </span>
      </TableCell>
      <TableCell className="px-5 py-3.5">
        {task.contactId ? (
          <div className="flex min-w-0 items-center gap-2 text-body-sm text-default [&>div]:flex [&>div]:min-w-0 [&>div]:!p-0 [&_span]:min-w-0 [&_span]:truncate [&_span]:font-normal">
            <LazyContactCell contactId={task.contactId} loadingIndicator={false} />
          </div>
        ) : (
          <span className="text-body-xs text-minimal">—</span>
        )}
      </TableCell>
      <TableCell className="px-5 py-3.5">
        <div className="flex min-w-0 items-center gap-2 text-body-sm text-default [&>div]:flex [&>div]:min-w-0 [&_span]:min-w-0 [&_span]:truncate [&_span]:font-normal">
          <TaskAssigneeLabel ownerId={task.ownerId} roleId={task.roleId} />
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap py-3.5 pl-5 pr-6">
        {task.dueDate ? (
          <span className={classNames(`text-body-sm`, overdue ? `font-semibold text-red-600` : `text-default`)}>
            {formatDueDate(task.dueDate)}
          </span>
        ) : (
          <span className="text-body-xs text-minimal">—</span>
        )}
      </TableCell>
    </TableRow>
  )
}

function GroupSection({
  group,
  onRowClick,
  onToggleStatus,
  selectedTaskId,
}: {
  group: Group
  onRowClick: (task: TaskJson) => void
  onToggleStatus: (task: TaskJson) => void
  selectedTaskId?: string
}) {
  const [open, setOpen] = useState(true)
  const label =
    group.marker.type === `owner` ? (
      <OwnerGroupLabel ownerId={group.marker.ownerId} roleId={group.marker.roleId} />
    ) : (
      <>{group.label}</>
    )

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mb-2 flex items-center gap-2 px-1 py-1 text-left transition-opacity hover:opacity-70"
      >
        <CaretRightIcon
          className={classNames(`h-3.5 w-3.5 text-subtle transition-transform`, open && `rotate-90`)}
          strokeWidth={2.5}
        />
        <GroupMarkerDot marker={group.marker} />
        <span className="text-body-sm font-semibold tracking-tight text-strong">{label}</span>
        <span className="text-label-xs font-medium tabular-nums text-minimal">{group.tasks.length}</span>
      </button>
      {open && (
        <div className="overflow-hidden rounded-md border border-default bg-surface shadow-elevation-1">
          <table className="w-full caption-bottom text-sm table-fixed min-w-[960px]">
            <TaskTableColGroup />
            <TableBody className="divide-y divide-subtle">
              {group.tasks.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onClick={() => onRowClick(task)}
                  onToggleStatus={() => onToggleStatus(task)}
                  isSelected={task.id === selectedTaskId}
                />
              ))}
            </TableBody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Top-level component ─────────────────────────────────────────────────────

export function TasksTable({
  query,
  initialSort: _initialSort,
  refreshTrigger,
  onTotalChange,
  onContactClick,
  onTaskUpdated,
  onContactIdsChange,
  onTaskTraversalItemsChange,
  highlightTaskId,
  groupBy = `status`,
  emptyState,
}: {
  query: string
  initialSort: { sortString: string }
  refreshTrigger?: number
  onTotalChange?: (total: number) => void
  onContactClick?: (contactId: string, highlightTaskId?: string) => void
  onTaskUpdated?: () => void
  onContactIdsChange?: (contactIds: string[]) => void
  onTaskTraversalItemsChange?: (items: { taskId: string; contactId: string }[]) => void
  highlightTaskId?: string
  /** Controlled by the parent (TasksComponent) so the Group-by dropdown can
   *  sit in the page toolbar alongside Search / Filters / Assigned-to-me. */
  groupBy?: GroupBy
  /** Owned by TasksComponent, which can see the active tab and the search/filter state. */
  emptyState?: ReactNode
}) {
  const [rawTasks, setRawTasks] = useState<TaskJson[]>([])
  // Client-side suppression list. The server's `deleteTask` mutation
  // confirms success, but the search index that backs the `tasks(...)`
  // list query lags by several seconds. Any list refetch in that window
  // returns the deleted task and it pops back into the UI. We keep a
  // local set of recently-deleted ids and filter them out of every
  // rendered list — cleared when the underlying query/filter changes
  // (i.e. the user navigates / changes filters) since by then enough
  // time has passed for the index to be in sync.
  const [deletedTaskIds, setDeletedTaskIds] = useState<Set<string>>(() => new Set())
  // Tasks saved here stay on screen as saved until the query changes or the
  // page reloads, even when a refetch no longer matches them: completing a
  // task on Open Tasks leaves the row in place, struck through, instead of
  // pulling it out from under the user. Same rule as the calendar.
  const [savedTasks, setSavedTasks] = useState<Map<string, TaskJson>>(() => new Map())
  // Tasks visible to the rest of this component — raw fetch plus kept saves,
  // minus recently-deleted ids. Everywhere else in this file reads `tasks`,
  // so we keep the name and route reads through this filtered view.
  const tasks = useMemo<TaskJson[]>(() => {
    const fetchedIds = new Set(rawTasks.map((t) => t.id))
    const kept = [...savedTasks.values()].filter((t) => !fetchedIds.has(t.id))
    const merged = [...rawTasks.map((t) => (t.id && savedTasks.get(t.id)) || t), ...kept]
    // Back into the list's own order (due date, as fetched) rather than at the end.
    if (kept.length) merged.sort((a, b) => moment(a.dueDate ?? 0).valueOf() - moment(b.dueDate ?? 0).valueOf())
    return merged.filter((t) => !t.id || !deletedTaskIds.has(t.id))
  }, [rawTasks, savedTasks, deletedTaskIds])
  const setTasks: typeof setRawTasks = setRawTasks
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(highlightTaskId)
  const { refresh } = useSidebarCounts()

  const fetchTasks = async () => {
    return gqlFetchList<TaskJson>(
      `tasks`,
      { search: query, pagination: { page: 0 }, sort: { field: `dueDate`, direction: `ASC` } },
      TASK_FIELDS,
    )
  }

  useEffect(() => {
    setIsLoading(true)
    // Query change = "real" navigation event; flush the local
    // delete-suppression set so we don't shadow deletes from prior tabs
    // forever. Kept saves go with it.
    setDeletedTaskIds(new Set())
    setSavedTasks(new Map())
    bg(async () => {
      const { hits, total } = await fetchTasks()
      setTasks(hits ?? [])
      setTotal(total ?? 0)
      setIsLoading(false)
    })
  }, [query])

  const mountRefreshTrigger = useRef(refreshTrigger)
  useEffect(() => {
    if (refreshTrigger === mountRefreshTrigger.current) return
    bg(async () => {
      const { hits, total } = await fetchTasks()
      setTasks(hits ?? [])
      setTotal(total ?? 0)
    })
  }, [refreshTrigger])

  useEffect(() => {
    onTotalChange?.(total)
  }, [total])

  const contactIds = useMemo(() => {
    const ids: string[] = []
    const seen = new Set<string>()
    for (const t of tasks) {
      if (!t.contactId || seen.has(t.contactId)) continue
      seen.add(t.contactId)
      ids.push(t.contactId)
    }
    return ids
  }, [tasks])
  useEffect(() => onContactIdsChange?.(contactIds), [contactIds, onContactIdsChange])

  const traversal = useMemo(
    () => tasks.flatMap((t) => (t.id && t.contactId ? [{ taskId: t.id, contactId: t.contactId }] : [])),
    [tasks],
  )
  useEffect(() => onTaskTraversalItemsChange?.(traversal), [traversal, onTaskTraversalItemsChange])

  const groups = useMemo(() => buildGroups(tasks, groupBy), [tasks, groupBy])
  const selectedTask = useMemo(() => tasks.find((t) => t.id === selectedTaskId), [tasks, selectedTaskId])

  // Flat list of tasks in display order (group-by order if grouped, otherwise
  // tasks as fetched). Used for keyboard navigation between rows when the
  // drawer is open — pressing ↑/↓ should walk to the visually-adjacent task,
  // not whatever a SQL-order would give us.
  const flatOrderedTasks = useMemo<TaskJson[]>(() => {
    if (groups) return groups.flatMap((g) => g.tasks)
    return tasks
  }, [groups, tasks])
  const selectedIndex = useMemo(
    () => (selectedTaskId ? flatOrderedTasks.findIndex((t) => t.id === selectedTaskId) : -1),
    [flatOrderedTasks, selectedTaskId],
  )
  const moveSelection = (delta: 1 | -1) => {
    if (flatOrderedTasks.length === 0) return
    if (selectedIndex < 0) {
      // No current selection — jump to first/last task instead of doing nothing.
      const target = delta === 1 ? flatOrderedTasks[0] : flatOrderedTasks[flatOrderedTasks.length - 1]
      if (target?.id) setSelectedTaskId(target.id)
      return
    }
    const next = Math.max(0, Math.min(flatOrderedTasks.length - 1, selectedIndex + delta))
    const target = flatOrderedTasks[next]
    if (target?.id) setSelectedTaskId(target.id)
  }

  // Keyboard nav: ↑/↓ (or j/k) walk through visible rows; X toggles the
  // selected task complete. Esc-to-close is handled by Radix Dialog already.
  // Skips events from form controls / contenteditable so editing fields in
  // the drawer doesn't fight the table for these keys.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      const tag = target.tagName
      if (tag === `INPUT` || tag === `TEXTAREA` || tag === `SELECT` || target.isContentEditable) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      // Only handle nav when a task is selected (the drawer's open). When
      // nothing is open, leave arrow keys alone for native page scrolling.
      if (!selectedTaskId) return
      switch (e.key) {
        case `ArrowDown`:
        case `j`:
          e.preventDefault()
          moveSelection(1)
          break
        case `ArrowUp`:
        case `k`:
          e.preventDefault()
          moveSelection(-1)
          break
        case `x`:
          if (selectedTask) {
            e.preventDefault()
            // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
            onToggleStatus(selectedTask)
          }
          break
      }
    }
    window.addEventListener(`keydown`, handler)
    return () => window.removeEventListener(`keydown`, handler)
  }, [selectedTaskId, selectedTask, flatOrderedTasks])

  // Scroll the selected row into view when the selection changes via keyboard
  // so the user doesn't lose track of where they are in the list.
  useEffect(() => {
    if (!selectedTaskId) return
    const row = document.querySelector(`[data-task-id="${selectedTaskId}"]`)
    if (row && `scrollIntoView` in row) {
      ;(row as HTMLElement).scrollIntoView({ block: `nearest`, behavior: `smooth` })
    }
  }, [selectedTaskId])

  // ─── Mutations ──────────────────────────────────────────────────────────────

  const updateTaskFields = async (taskId: string, input: TaskFieldInput): Promise<boolean> => {
    const previous = rawTasks
    const previousSaved = savedTasks
    setTasks((prev) => prev.map((t) => (t.id === taskId ? ({ ...t, ...(input as Partial<TaskJson>) } as TaskJson) : t)))
    // A kept task is not in the fetch, so it takes the optimistic patch here.
    setSavedTasks((prev) => {
      const kept = prev.get(taskId)
      return kept ? new Map(prev).set(taskId, { ...kept, ...(input as Partial<TaskJson>) } as TaskJson) : prev
    })
    const { data, error } = await gqlMutation(UPDATE_TASK_MUTATION, {
      id: taskId,
      input,
    })
    if (error || !data?.updateTask) {
      setTasks(previous)
      setSavedTasks(previousSaved)
      notifyError(`Failed to save${error?.message ? `: ${error.message}` : ``}`)
      return false
    }
    const saved = modelData(getFragmentData(TASK_FIELDS_FRAGMENT, data.updateTask))
    setTasks((prev) => prev.map((t) => (t.id === taskId ? saved : t)))
    setSavedTasks((prev) => new Map(prev).set(taskId, saved))
    refresh()
    onTaskUpdated?.()
    return true
  }

  const deleteTask = async (taskId: string): Promise<boolean> => {
    // Add to the suppression set BEFORE the mutation so even if a refetch
    // races in (e.g. another component bumping refreshTrigger), the row
    // stays hidden. We also drop it from rawTasks for the same effect on
    // the immediate render.
    setDeletedTaskIds((prev) => {
      const next = new Set(prev)
      next.add(taskId)
      return next
    })
    setRawTasks((prev) => prev.filter((t) => t.id !== taskId))
    const { data, error } = await gqlMutation(DELETE_TASK_MUTATION, {
      id: taskId,
    })
    if (error || !data?.deleteTask) {
      // Roll back the suppression so the row reappears (the mutation
      // didn't actually delete).
      setDeletedTaskIds((prev) => {
        const next = new Set(prev)
        next.delete(taskId)
        return next
      })
      notifyError(`Failed to delete${error?.message ? `: ${error.message}` : ``}`)
      return false
    }
    refresh()
    return true
  }

  const onToggleStatus = (task: TaskJson) => {
    if (!task.id) return
    const next = task.status === `closed` ? `open` : `closed`
    bg(
      (async () => {
        const ok = await updateTaskFields(task.id!, { status: next })
        // Only surface the success toast after the mutation actually lands — if
        // it fails, `updateTaskFields` already shows an error toast and rolls
        // back the optimistic state, so we'd be lying with a success here.
        if (ok) notifySuccess(next === `closed` ? `Task marked complete` : `Task reopened`)
      })(),
    )
  }

  const openContact = (task: TaskJson) => {
    if (task.contactId && onContactClick) onContactClick(task.contactId, task.id)
  }

  const openCreateFollowUp = (contact: ContactInfo) => {
    const defaultContact = {
      id: contact.id,
      name: contact.name || `Unknown`,
      locationId: contact.locationId,
    } as ContactJson
    showModal(
      <QuickCreateTaskModal defaultContact={defaultContact} onTaskCreatedOrUpdated={() => onTaskUpdated?.()} />,
      { hideTitle: true, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
    )
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    // No internal scroll — the parent scroll container in TasksComponent owns
    // both axes so a single horizontal scrollbar at the bottom moves every
    // card in lockstep. `min-w-[960px]` here propagates the table's intrinsic
    // width up to the cards so they stay aligned (and don't independently
    // overflow their shadcn Table wrappers).
    //
    // `min-h-full shrink-0`, NOT `h-full`: this div is a flex child of the
    // parent `overflow-auto` scroll container (a flex-col, per AppShell's
    // edge-scroll `EDGE_SCROLL_PAGES`). Two things would otherwise kill the
    // scroll:
    //   - `h-full` pins this to exactly the container height, and the
    //     `overflow-hidden` table cards below (flex `min-height:auto` → 0)
    //     shrink to fit and clip their rows.
    //   - even without `h-full`, as a flex item this div's default
    //     `flex-shrink: 1` lets the column shrink it down to the container's
    //     height, so its content never exceeds the viewport and the scrollbar
    //     never engages.
    // `shrink-0` stops the shrink so content keeps its natural height and
    // overflows the scroll container; `min-h-full` still fills the full height
    // when there are only a few rows.
    <div className="flex min-h-full min-w-[960px] shrink-0 flex-col gap-4">
      {/* Content */}
      {isLoading && tasks.length === 0 ? (
        <div className="py-12 text-center text-body-sm text-subtle">Loading…</div>
      ) : tasks.length === 0 ? (
        // Every `groupBy` lands here: at zero rows there are no groups to draw,
        // so the ungrouped frame is the only one that makes sense.
        <div className="overflow-hidden rounded-md border border-default bg-surface shadow-elevation-1">
          <table className="w-full caption-bottom text-sm table-fixed min-w-[960px]">
            <TaskTableColGroup />
            <TableHeader>
              <TaskHeaderCells />
            </TableHeader>
          </table>
          {emptyState ?? <TableEmptyState title="No tasks found" />}
        </div>
      ) : groups ? (
        <div className="flex flex-col gap-5">
          {/* Sticky column header table */}
          <div className="sticky top-0 z-10 overflow-hidden rounded-md border border-default bg-surface">
            <table className="w-full caption-bottom text-sm table-fixed min-w-[960px]">
              <TaskTableColGroup />
              <TableHeader>
                <TaskHeaderCells />
              </TableHeader>
            </table>
          </div>
          {/* Each group is its own card */}
          {groups.map((g) => (
            <GroupSection
              key={g.key}
              group={g}
              onRowClick={(t) => t.id && setSelectedTaskId(t.id)}
              onToggleStatus={onToggleStatus}
              selectedTaskId={selectedTaskId}
            />
          ))}
        </div>
      ) : (
        // Ungrouped: single table card with header + body
        <div className="overflow-hidden rounded-md border border-default bg-surface shadow-elevation-1">
          <table className="w-full caption-bottom text-sm table-fixed min-w-[960px]">
            <TaskTableColGroup />
            <TableHeader>
              <TaskHeaderCells />
            </TableHeader>
            <TableBody className="divide-y divide-subtle">
              {tasks.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  onClick={() => t.id && setSelectedTaskId(t.id)}
                  onToggleStatus={() => onToggleStatus(t)}
                  isSelected={t.id === selectedTaskId}
                />
              ))}
            </TableBody>
          </table>
        </div>
      )}

      {/* Task detail drawer — slide-in from right when a row is clicked. */}
      <Sheet
        open={!!selectedTask}
        onOpenChange={(open) => {
          if (!open) setSelectedTaskId(undefined)
        }}
      >
        <SheetContent side="right" className="w-full p-0 sm:w-3/4">
          {selectedTask && (
            <TaskDetailPane
              task={selectedTask}
              onCheck={() => onToggleStatus(selectedTask)}
              onClose={() => setSelectedTaskId(undefined)}
              onOpenContact={() => openContact(selectedTask)}
              onCreateFollowUp={openCreateFollowUp}
              onUpdate={(input) =>
                selectedTask.id ? updateTaskFields(selectedTask.id, input) : Promise.resolve(false)
              }
              onDelete={deleteTask}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
